import React, { useEffect, useState } from 'react';
import HealthDot, { HEALTH_IN_PROGRESS } from '../ui/HealthDot.jsx';

const SECTIONS = [
  { id: 'vital-signs', label: 'Vital signs' },
  { id: 'meetings',    label: 'Meetings' },
  { id: 'feedback',    label: 'Client feedback' },
  { id: 'team',        label: 'Team' },
  { id: 'time',        label: 'Time tracking' },
  { id: 'hours-budget', label: 'Hours budget' },
  { id: 'docs',        label: 'Docs & repo' }
];

export default function JumpNav({ project }) {
  const [active, setActive] = useState('vital-signs');
  const [healthHover, setHealthHover] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter(e => e.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
        if (visible[0]) setActive(visible[0].target.id);
      },
      { rootMargin: '-80px 0px -60% 0px', threshold: 0 }
    );
    SECTIONS.forEach(s => {
      const el = document.getElementById(s.id);
      if (el) observer.observe(el);
    });
    return () => observer.disconnect();
  }, []);

  function jumpTo(id, e) {
    e.preventDefault();
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  const bd = project.health_breakdown || {};

  return (
    <nav aria-label="On this page" style={{ position: 'sticky', top: 80 }}>
      <div
        className="rounded-lg border"
        style={{ background: '#fff', borderColor: '#ECEBE5', padding: '10px 10px 8px' }}
      >
        <div
          className="text-[10px] uppercase mb-1.5"
          style={{ color: '#9A9A95', letterSpacing: '0.08em', paddingLeft: 2 }}
        >
          On this page
        </div>
        <ul style={{ marginLeft: -10, marginRight: -10 }}>
          {SECTIONS.map(s => {
            const isActive = active === s.id;
            return (
              <li key={s.id}>
                <a
                  href={`#${s.id}`}
                  onClick={(e) => jumpTo(s.id, e)}
                  aria-current={isActive ? 'location' : undefined}
                  className="block transition-colors"
                  style={{
                    color: isActive ? '#1A1A1A' : '#6B6B68',
                    fontWeight: isActive ? 500 : 400,
                    fontSize: 12,
                    lineHeight: 1.35,
                    padding: '4px 10px',
                    borderLeft: `2px solid ${isActive ? '#1A1A1A' : 'transparent'}`,
                    background: isActive ? '#F5F4EE' : 'transparent',
                    textDecoration: 'none'
                  }}
                >
                  {s.label}
                </a>
              </li>
            );
          })}
        </ul>
      </div>

      <div
        className="rounded-lg border mt-2"
        onMouseEnter={() => setHealthHover(true)}
        onMouseLeave={() => setHealthHover(false)}
        style={{ position: 'relative', background: '#fff', borderColor: '#ECEBE5', padding: '10px 12px', cursor: 'default' }}
      >
        {healthHover && (
          <span
            role="tooltip"
            style={{
              position: 'absolute',
              bottom: 'calc(100% + 6px)',
              left: '50%',
              transform: 'translateX(-50%)',
              background: '#1A1A1A',
              color: '#fff',
              fontSize: 11,
              fontWeight: 500,
              padding: '4px 8px',
              borderRadius: 4,
              whiteSpace: 'nowrap',
              pointerEvents: 'none',
              zIndex: 20,
              boxShadow: '0 2px 6px rgba(0,0,0,0.18)'
            }}
          >
            {HEALTH_IN_PROGRESS}
          </span>
        )}
        <div
          className="text-[10px] uppercase mb-2"
          style={{ color: '#9A9A95', letterSpacing: '0.08em' }}
        >
          Health score
        </div>
        <div className="flex items-baseline gap-1.5 mb-2">
          <HealthDot score={project.health} size={9} disabled />
          <span
            style={{
              color: '#9A9A95',
              fontWeight: 600,
              fontSize: 22,
              lineHeight: 1,
              fontVariantNumeric: 'tabular-nums'
            }}
          >
            {project.health}
          </span>
          <span style={{ color: '#9A9A95', fontSize: 10.5 }}>/ 100</span>
        </div>
        <div style={{ borderTop: '1px solid #ECEBE5', paddingTop: 6 }}>
          {Object.entries(bd).map(([k, v]) => (
            <div
              key={k}
              className="flex items-center justify-between"
              style={{ color: '#9A9A95', fontSize: 11, padding: '3px 0' }}
            >
              <span style={{ textTransform: 'capitalize' }}>{k.replace(/_/g, ' ')}</span>
              <span
                style={{
                  color: '#9A9A95',
                  fontWeight: 600,
                  fontVariantNumeric: 'tabular-nums'
                }}
              >
                {v}
              </span>
            </div>
          ))}
        </div>
      </div>
    </nav>
  );
}
